import {
  tokens,
  cardBase,
  cardTitleStyle,
  cardSubStyle,
  sectionLabelStyle,
  Badge,
  Th,
  Td,
  StatusPill,
  PrimaryBtn,
  OutlineBtn,
  MetricCard,
  boldCellStyle,
} from "./dashboardKit";

// ============================================================
// Data
// ============================================================

const WEEKS = [
  { week: "Week 1", inflows: "1,200,000", outflows: "950,000", net: "+250,000", positive: true },
  { week: "Week 2", inflows: "900,000", outflows: "1,050,000", net: "−150,000", positive: false },
  { week: "Week 3", inflows: "1,050,000", outflows: "1,100,000", net: "−50,000", positive: false },
  { week: "Week 4", inflows: "1,300,000", outflows: "1,000,000", net: "+300,000", positive: true },
  { week: "Week 5", inflows: "1,000,000", outflows: "1,150,000", net: "−150,000", positive: false },
];

const BAR_DATA = [
  { w: "W1", val: 250000, pos: true },
  { w: "W2", val: -150000, pos: false },
  { w: "W3", val: -50000, pos: false },
  { w: "W4", val: 300000, pos: true },
  { w: "W5", val: -150000, pos: false },
];
const MAX_ABS = Math.max(...BAR_DATA.map((d) => Math.abs(d.val)));

const AGING = [
  { bucket: "0–30 days", amount: "AED 2.1M", share: "49%", danger: false },
  { bucket: "31–60 days", amount: "AED 1.1M", share: "26%", danger: false },
  { bucket: "61–90 days", amount: "AED 650k", share: "15%", danger: false },
  { bucket: "> 90 days", amount: "AED 450k", share: "10%", danger: true },
];

const CUSTOMER_RISK = [
  { customer: "RetailCo MENA", ar: "320,000", past: 74, risk: "High", next: "Escalate & revise terms" },
  { customer: "GulfMart", ar: "210,000", past: 45, risk: "Medium", next: "Call & agree plan" },
  { customer: "CityElectro", ar: "95,000", past: 28, risk: "Low", next: "Automated reminder" },
];

// ============================================================
// Small local pieces
// ============================================================

const MiniBarChart = () => (
  <div style={{ display: "flex", gap: 8, alignItems: "flex-end", height: 80, marginTop: 12 }}>
    {BAR_DATA.map((d) => {
      const pct = (Math.abs(d.val) / MAX_ABS) * 70;
      return (
        <div key={d.w} style={{ display: "flex", flexDirection: "column", alignItems: "center", flex: 1, gap: 4 }}>
          <div
            style={{
              width: "100%",
              height: pct,
              background: d.pos ? "#3B82F6" : "#F87171",
              borderRadius: "4px 4px 0 0",
              minHeight: 8,
            }}
          />
          <span style={{ fontFamily: tokens.fontRegular, fontSize: 11, color: tokens.mutedColor, lineHeight: "16px" }}>{d.w}</span>
        </div>
      );
    })}
  </div>
);

const AlertCard = ({ tone, title, sub, actions }) => {
  const toneMap = {
    high: { bg: "#FFF1F2", border: "#FECDD3" },
    medium: { bg: "#FFFBEB", border: "#FDE68A" },
  };
  const t = toneMap[tone];
  return (
    <div style={{ background: t.bg, border: `1px solid ${t.border}`, borderRadius: 8, padding: "12px 14px", marginTop: 10 }}>
      <div style={{ fontFamily: tokens.fontBold, fontWeight: 700, fontSize: 13, color: tokens.titleColor, lineHeight: "20px" }}>{title}</div>
      <div style={{ ...cardSubStyle, marginBottom: 10 }}>{sub}</div>
      <div style={{ display: "flex", gap: 8 }}>
        {actions.map((a, i) => (i === 0 ? <PrimaryBtn key={a}>{a}</PrimaryBtn> : <OutlineBtn key={a}>{a}</OutlineBtn>))}
      </div>
    </div>
  );
};

// ============================================================
// Component
// ============================================================

export default function CfoDashboard() {
  return (
    <div className="py-1">
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
        <MetricCard badge="QUERY" title="Consolidated Cash" value="AED 5.2M" sub="Across all operating accounts · Floor AED 2.0M" link="Open Cashflow" />
        <MetricCard
          badge="QUERY"
          title="Net Cash This Month"
          value="AED 80,060"
          sub={<span style={{ color: "#059669" }}>Better than budget (≈ AED 120k)</span>}
          link="View drivers"
        />
        <MetricCard badge="QUERY" title="AP Outstanding" value="AED 3.2M" sub="All vendors · Overdue AED 640k" link="Open Payables" />
        <MetricCard badge="QUERY" title="AR Outstanding" value="AED 4.3M" sub="DSO 56 days (target 45)" link="Open Receivables" />
      </div>
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 24 }}>
        <MetricCard badge="AI" title="Cash Runway (Base)" value="128 days" alert="↓ from 187 — driven by 24 Dec dip" link="See forecast" />
        <MetricCard badge="QUERY" title="Covenant Headroom" value="1 Watch" sub="Leverage 2.7× — (limit 3.0×)" link="Covenant detail" />
        <MetricCard badge="AI" title="Predicted Shortfall Risk" value="Low" sub="Next 30 days · 8% probability" link="Stress test" />
      </div>

      {/* ── Priority Actions & Live Snapshot ── */}
      <div style={sectionLabelStyle}>Priority Actions &amp; Live Snapshot</div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 24 }}>
        {/* AI Alerts */}
        <div style={{ flex: 1, minWidth: 320, ...cardBase }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
            <div>
              <div style={cardTitleStyle}>AI Alerts &amp; Suggested Actions</div>
              <div style={cardSubStyle}>Ranked by impact on liquidity · top 3 shown</div>
            </div>
            <Badge type="AI" />
          </div>

          <AlertCard
            tone="high"
            title="Forecast deviation — cash dips below AED 2M on 24 Dec"
            sub="Runway falls to 128 days if no action."
            actions={["Open Cashflow", "Simulate cuts"]}
          />
          <AlertCard
            tone="medium"
            title="6 payments pending approval > 5 days"
            sub="AED 310k total; 3 are critical vendors."
            actions={["Prioritize", "Auto-rules"]}
          />
          <AlertCard
            tone="medium"
            title="Collections slowdown in Retail (DSO 48→56)"
            sub="Focus 61–90 day bucket."
            actions={["Receivables", "Call list"]}
          />

          <div style={{ marginTop: 12, ...cardSubStyle }}>
            Showing top 3 of 5.{" "}
            <a href="#" style={{ fontFamily: tokens.fontSemibold, color: "#1967D2", textDecoration: "none", fontWeight: 600 }}>
              View all alerts →
            </a>
          </div>
        </div>

        {/* Cashflow Snapshot */}
        <div style={{ flex: 1, minWidth: 320, ...cardBase }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <div>
              <div style={cardTitleStyle}>Cashflow Snapshot (Next 5 Weeks)</div>
              <div style={cardSubStyle}>Net cash by week · Base scenario</div>
            </div>
            <Badge type="QUERY" />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
            <thead>
              <tr>
                <Th>Week</Th>
                <Th>Inflows</Th>
                <Th>Outflows</Th>
                <Th>Net</Th>
              </tr>
            </thead>
            <tbody>
              {WEEKS.map((row) => (
                <tr key={row.week}>
                  <Td>{row.week}</Td>
                  <Td>{row.inflows}</Td>
                  <Td>{row.outflows}</Td>
                  <Td style={boldCellStyle(!row.positive)}>{row.net}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          <MiniBarChart />
        </div>
      </div>

      {/* ── Working Capital Focus ── */}
      <div style={sectionLabelStyle}>Working Capital Focus</div>
      <div style={{ display: "flex", gap: 16, flexWrap: "wrap", marginBottom: 16 }}>
        {/* Collections & Aging */}
        <div style={{ flex: 1, minWidth: 300, ...cardBase }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <div>
              <div style={cardTitleStyle}>Collections &amp; Aging Summary</div>
              <div style={cardSubStyle}>DSO 56 days (↑ from 48) · AR AED 4.3M</div>
            </div>
            <Badge type="QUERY" />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
            <thead>
              <tr>
                <Th>Bucket</Th>
                <Th>Amount</Th>
                <Th>Share</Th>
              </tr>
            </thead>
            <tbody>
              {AGING.map((row) => (
                <tr key={row.bucket}>
                  <Td>{row.bucket}</Td>
                  <Td style={boldCellStyle(row.danger)}>{row.amount}</Td>
                  <Td style={{ color: tokens.mutedColor }}>{row.share}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, ...cardSubStyle }}>Target: reduce &gt;60 day bucket by AED 400k in 45 days.</div>
        </div>

        {/* Customer Risk */}
        <div style={{ flex: 1, minWidth: 300, ...cardBase }}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 2 }}>
            <div>
              <div style={cardTitleStyle}>Customer Risk &amp; Next-Best Action</div>
              <div style={cardSubStyle}>Cross-cut from Receivables · AI-prioritised</div>
            </div>
            <Badge type="AI" />
          </div>
          <table style={{ width: "100%", borderCollapse: "collapse", marginTop: 14 }}>
            <thead>
              <tr>
                <Th>Customer</Th>
                <Th>AR</Th>
                <Th>Past due</Th>
                <Th>Risk</Th>
                <Th>Next step</Th>
              </tr>
            </thead>
            <tbody>
              {CUSTOMER_RISK.map((row) => (
                <tr key={row.customer}>
                  <Td style={boldCellStyle()}>{row.customer}</Td>
                  <Td>{row.ar}</Td>
                  <Td>{row.past}</Td>
                  <Td>
                    <StatusPill level={row.risk} />
                  </Td>
                  <Td style={{ color: tokens.mutedColor, fontSize: 12 }}>{row.next}</Td>
                </tr>
              ))}
            </tbody>
          </table>
          <div style={{ marginTop: 12, ...cardSubStyle }}>
            Automate low-risk; free team for <span style={{ fontFamily: tokens.fontSemibold, color: "#1967D2", fontWeight: 600 }}>high-touch accounts</span>.
          </div>
        </div>
      </div>

      {/* ── Footer note ── */}
      <div style={{ ...cardBase, borderRadius: 10, padding: "12px 18px", ...cardSubStyle, display: "flex", alignItems: "center", gap: 8 }}>
        Annotated prototype — Working Capital Pulse table moved into the Cashflow hub to reduce density on this overview. Each card now carries a&nbsp;
        <Badge type="QUERY" />
        &nbsp;/&nbsp;
        <Badge type="AI" />
        &nbsp;source tag.
      </div>
    </div>
  );
}